import React from 'react';
import { arrayOrArray, EventEmitter } from '@apia/util';
import { classToValidate, Select } from '@apia/validations';
import { TApiaApiField } from '../ApiaApiHandler';

export type TOption = {
  content: string;
  value: string;
};

class ApiaApiSelectOptions extends EventEmitter<{
  setOptions: {
    fieldName: string;
    options: TOption[];
  };
}> {}

export const apiaApiSelectOptions = new ApiaApiSelectOptions();

export const ApiaApiSelect = (props: TApiaApiField) => {
  const element = React.useMemo(() => props.element, [props.element]);
  const className = React.useMemo(
    () =>
      element.class ? `handler__select ${element.class}` : 'handler__select',
    [element.class],
  );
  const [options, setOptions] = React.useState<
    {
      value: string;
      label: string;
    }[]
  >(
    arrayOrArray(element.options?.option).map((currOption) => {
      return { value: currOption.value, label: currOption.content };
    }),
  );
  const validationRules = React.useMemo(
    () => ({
      required: element.mandatory,
      ...classToValidate(element.class),
    }),
    [element.class, element.mandatory],
  );

  React.useEffect(() => {
    const unsuscribe = apiaApiSelectOptions.on('setOptions', (ev) => {
      if (ev.fieldName === element.name) {
        setOptions(
          ev.options.map((currOption) => ({
            value: currOption.value,
            label: currOption.content,
          })),
        );
      }
    });

    return () => {
      unsuscribe();
    };
  }, [element.name]);

  const initialValue = React.useMemo(
    () =>
      options.find((current) => current.value === props.element.value)?.value ??
      options[0]?.value ??
      '',
    [options, props.element.value],
  );

  return (
    <Select
      className={className}
      name={element.id || element.name}
      label={element.text}
      title={element.title || element.text}
      initialValue={initialValue}
      disabled={element.readonly || element.disabled}
      validationRules={validationRules}
      options={options}
      onChange={element.onChange}
    />
  );
};

ApiaApiSelect.displayName = 'ApiaApiSelect';
